import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { BookOpen, Clapperboard, CalendarDays, Inbox, Phone, ChevronRight, Search, CalendarPlus, CalendarRange } from 'lucide-react'
import { api } from '../../lib/api.js'
import EqMark from '../../components/EqMark.jsx'
import { BOX_NAME, BOX_DESC } from '../../lib/suggestionBox.js'
import { APP_NAME as ASSISTANT_NAME, APP_DESC as ASSISTANT_DESC } from '../assistant/lib.js'

/**
 * The hub. One address, two apps: CDM Information (the repository) and
 * CDM Session Log. Everything else in the shell stays one tap away, so
 * this page is a front door, not a dashboard.
 */
export default function HomePage() {
  const me = useQuery({ queryKey: ['me'], queryFn: () => api('/auth/me'), staleTime: Infinity })
  const user = me.data?.user

  // Red dot on the Session Log tile (Charles, 2026-08-04): the admin's
  // landing-page signal that the log check has days to review. Shares the
  // ['sl-check'] cache with the check screens; empty for non-admins.
  const check = useQuery({
    queryKey: ['sl-check'],
    queryFn: () => api('/log/check'),
    enabled: user?.role === 'admin',
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  })
  const checkAlert = (check.data?.days || []).reduce((n, d) => n + d.deficit, 0) > 0

  // Navy dot on the Suggestion Box tile (Charles, 2026-08-06): replies or
  // verdicts on YOUR suggestions since you last opened the box. Navy, not
  // red — it's news, not an alarm (red stays armed-only). Shares
  // Session Builder: bookings typed into Google that haven't been tidied
  // yet (Charles, 2026-10-04). Shared ['sa-untidy'] with the rail.
  const untidy = useQuery({
    queryKey: ['sa-untidy'],
    queryFn: () => api('/assistant/untidy'),
    enabled: user?.role === 'admin',
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
    refetchInterval: 5 * 60 * 1000,
  })
  const toTidy = untidy.data?.count || 0
  // …and projects with a finished session not ticked off as billed
  // (Charles, 2026-10-07). Shared ['sa-billing-count'] with the rail.
  const billing = useQuery({
    queryKey: ['sa-billing-count'],
    queryFn: () => api('/assistant/billing/count'),
    enabled: user?.role === 'admin',
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
    refetchInterval: 5 * 60 * 1000,
  })
  const toBill = billing.data?.count || 0
  // ['box-activity'] with the shell rail; opening the box clears it.
  const boxActivity = useQuery({
    queryKey: ['box-activity'],
    queryFn: () => api('/box/activity'),
    enabled: Boolean(user),
    staleTime: 60 * 1000,
    refetchOnWindowFocus: true,
    refetchInterval: 5 * 60 * 1000,
  })
  const boxAlert = (boxActivity.data?.count || 0) > 0

  return (
    <div className="relative mx-auto max-w-md">
      {/* Desktop's magnifier — the phone brand bar carries its own
          (AppShell), so this one is hidden below md. */}
      <Link
        to="/search"
        aria-label="Search"
        className="absolute right-0 top-0 hidden h-10 w-10 items-center justify-center rounded-full text-navy hover:bg-black/5 md:flex"
      >
        <Search size={22} strokeWidth={2} />
      </Link>
      <div className="pb-8 pt-8 text-center">
        <div className="flex justify-center">
          <EqMark heights={[10, 18, 8, 24, 14, 28, 12, 20, 9]} width={5} gap={3} animate />
        </div>
        <h1 className="mt-3 text-3xl font-semibold text-ink">CDM Sound Studios</h1>
        {user && (
          <p className="mt-1 text-sm text-muted">
            Signed in as {user.name} · {user.role}
          </p>
        )}
      </div>

      <div className="space-y-3">
        <AppTile
          to="/guides"
          markClass="bg-navy"
          Icon={BookOpen}
          title="CDM Information"
          desc="Tutorials, client specs, procedures & shared logins"
        />
        <AppTile
          to="/log"
          markClass="bg-cyan"
          Icon={Clapperboard}
          title="CDM Session Log"
          desc="Who set up what, when — and recall any setup"
          alert={checkAlert}
        />
        {/* Managerial — the office's booking tool (Charles, 2026-10-02:
            "just me and Tucker"). Engineers see the result on their own
            calendar, so the tile isn't theirs to see. Just below the
            Session Log (Charles, 2026-10-14). */}
        {user?.role === 'admin' && (
          <AppTile
            to="/assistant"
            markClass="bg-navy"
            Icon={CalendarPlus}
            title={ASSISTANT_NAME}
            desc={ASSISTANT_DESC}
            badge={[toTidy && `${toTidy} to tidy`, toBill && `${toBill} to bill`].filter(Boolean).join(' · ') || null}
          />
        )}
        {/* Session Builder v3 (Charles, 2026-10-08): the new design, on the
            same bookings, to try out in practice beside the current one.
            Charles's account only since 2026-10-09, nobody's since 2026-10-14
            (api lib/builder-v3.js — the list there is empty). */}
        {user?.builderV3 && (
          <AppTile
            to="/builder"
            markClass="bg-cyan"
            Icon={CalendarRange}
            title="Session Builder v3"
            desc="The new design — same bookings, to try out"
          />
        )}
        <AppTile
          to="/calendar"
          markClass="bg-ink"
          Icon={CalendarDays}
          title="CDM Calendar"
          desc="Today’s rooms and bookings at a glance"
        />
        <AppTile
          to="/box"
          markClass="bg-music-fg"
          Icon={Inbox}
          title={BOX_NAME}
          desc={BOX_DESC}
          alert={boxAlert}
          alertClass="bg-navy"
        />
        <AppTile
          to="/phone"
          markClass="bg-film-fg"
          Icon={Phone}
          title="Phone Log"
          desc="A call came in — jot it or say it, off to bookings@"
        />
      </div>
    </div>
  )
}

function AppTile({ to, markClass, Icon, title, desc, alert = false, alertClass = 'bg-danger', badge = null }) {
  return (
    <Link to={to} className="card flex items-center gap-4 p-4 active:bg-grouped">
      <span className={`relative flex h-12 w-12 shrink-0 items-center justify-center rounded-xl text-white ${markClass}`}>
        <Icon size={24} />
        {alert && <span className={`absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full border-2 border-white ${alertClass}`} />}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block font-serif text-lg font-semibold text-ink">
          {title}
          {/* A count in words, navy = news not alarm (e.g. "3 to tidy"). */}
          {badge && <span className="ml-2 inline-block rounded-full bg-navy px-2 py-0.5 align-middle font-sans text-[11px] font-semibold text-white">{badge}</span>}
        </span>
        <span className="mt-0.5 block text-sm text-muted">{desc}</span>
      </span>
      <ChevronRight size={18} className="shrink-0 text-faint" />
    </Link>
  )
}
